// app/loading.tsx or components/Loading.tsx

import { useEffect, useState } from 'react';
import Image from 'next/image';
import classnames from 'classnames';
import Loader from 'public/images/loader.gif';

type LoadingProps = {
  // Compact variant for inline slots (dropdown triggers, buttons) where the
  // full-screen wrapper below would blow out the layout.
  inline?: boolean;
  // Covers the nearest `relative` parent (e.g. a form) while its request is in
  // flight: dims it, blocks clicks on it and tells the user what is happening.
  // It doesn't block the keyboard — disable controls that must not fire.
  overlay?: boolean;
  // Visible (and announced) status text for the overlay variant.
  label?: string;
  size?: number;
};

// The overlay blocks clicks as soon as it mounts but only fades in after this
// delay, so a request that settles quickly doesn't flash it — the submit
// button's own "Sending" state covers that gap.
const OVERLAY_FADE_IN_DELAY_MS = 300;

function LoadingOverlay({ label }: { label?: string }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), OVERLAY_FADE_IN_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      role="status"
      aria-live="polite"
      className={classnames(
        'absolute inset-0 z-20 flex flex-col items-center justify-center px-4 text-center bg-white bg-opacity-90 cursor-wait transition-opacity duration-200',
        visible ? 'opacity-100' : 'opacity-0'
      )}
    >
      {/* Sticky so the loader stays on screen when a tall form is scrolled
          and its center sits outside the viewport (short phones, landscape). */}
      <div className="sticky flex flex-col items-center top-4 bottom-4">
        {/* The gif has an opaque white background; multiply lets it blend into
            the translucent overlay instead of showing as a white square. */}
        <Image
          src={Loader}
          alt=""
          width={80}
          height={80}
          className="object-contain w-16 h-16 md:w-20 md:h-20 mix-blend-multiply"
        />
        {label && (
          <span className="mt-3 text-16-26-0.3 text-black font-sohneKraftig">
            {label}
          </span>
        )}
      </div>
    </div>
  );
}

export default function Loading({
  inline,
  overlay,
  label,
  size = 14,
}: LoadingProps) {
  if (overlay) {
    return <LoadingOverlay label={label} />;
  }

  if (inline) {
    return (
      <span role="status" className="inline-flex items-center align-middle">
        <Image
          src={Loader}
          alt="Loading"
          width={size}
          height={size}
          className="object-contain"
        />
      </span>
    );
  }

  return (
    <div className="w-screen h-screen flex items-center justify-center bg-white">
      <Image
        src={Loader}
        alt="Loading animation"
        width={200}
        height={200}
        className="object-contain max-w-[60vw] max-h-[60vh]"
        priority
      />
    </div>
  );
}
